import React from 'react';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { isEqual } from 'lodash';

import { PageHeader } from '../../../components/PageHeader';
import { Button } from '../../../components/PageHeader/Button';
import { TextInput } from '../../../components/PageHeader/TextInput';
import { useGlobalPageSize } from '../../../utils/tableUtils';
import Table3, { columnHelper } from '../../../containers/Table/Table3';
import Strings from '../../../Strings';
import { actions } from '../Users/redux/actions';
import { HEADER_BUTTON_BLUE } from '../../../constants';
import OrgSelect from '../../../components/OrgSelect/OrgSelect';
import AdvancedFilters, { Filter } from '../../../components/AdvancedFilters/AdvancedFilters';
import { getFiltersDefinition } from '../../../components/AdvancedFilters/FiltersDefinition';
import AdvancedFiltersBar from '../../../components/AdvancedFilters/AdvancedFiltersBar';
import { getRelevantFilters } from '../../../components/AdvancedFilters/helpers';
import { openModalAction } from '../../../actions/modal';
import { isCanceledResponse } from '../../../middleware/api';

function AdminRolesTab(props) {
  const globalPageSize = useGlobalPageSize();
  const { getRoles, onNavigate, orgIds, filters, addRole } = props;

  const [isLoading, setIsLoading] = React.useState(false);
  const [roles, setRoles] = React.useState([]);
  const [pagination, setPagination] = React.useState(null);

  // Filter state
  const [searchTerm, setSearchTerm] = React.useState('');
  const [sortColumn, setSortColumn] = React.useState('title');
  const [sortType, setSortType] = React.useState('ascending');
  const [currentPage, setCurrentPage] = React.useState(1);

  const pageRequest = React.useRef();

  const loadRoles = React.useCallback(force => {
    if (!getRoles) return;

    setIsLoading(true);

    const params = {
      offset: (currentPage - 1) * globalPageSize,
      limit: globalPageSize,
      sortColumn,
      sortType,
    };

    if (searchTerm) params.search = searchTerm;
    if (orgIds && orgIds.length > 0) params.org_uuid = orgIds;

    const archetypeFilter = filters && getRelevantFilters(filters?.filter(f => f.group === 'archetypeExtended'))?.[0];
    if (archetypeFilter && archetypeFilter.value !== 'all') {
      params.archetype = archetypeFilter.value;
    } else {
      delete params.archetype;
    }

    if (pageRequest.current?.org_uuid !== params.org_uuid
      || pageRequest.current?.search !== params.search
      || pageRequest.current?.archetype !== params.archetype
      || pageRequest.current?.sortColumn !== params.sortColumn
      || pageRequest.current?.sortType !== params.sortType) {
      setCurrentPage(1);
      params.offset = 0; // Reset offset when filters change
    }

    if (isEqual(params, pageRequest.current) && !force) {
      setIsLoading(false);
      return; // No need to reload if params haven't changed
    }
    pageRequest.current = params;

    getRoles(params).then(resp => {
      // Superseded by a newer search/page/sort request, which will fill the table.
      if (isCanceledResponse(resp)) {
        return;
      }
      const data = resp?.response || {};
      const rolesData = Array.isArray(data) ? data : data.data || [];
      setRoles(rolesData);
      setPagination({
        totalRecords: data.totalRecords || rolesData.length,
        offset: (currentPage - 1) * globalPageSize,
        limit: globalPageSize,
      });
      setIsLoading(false);
    }).catch(() => {
      setIsLoading(false);
    });
  }, [currentPage, globalPageSize, searchTerm, filters, orgIds, sortColumn, sortType, getRoles, filters]);

  React.useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const onSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const onOffsetChange = (newOffset) => {
    const newPage = Math.floor(newOffset / globalPageSize) + 1;
    setCurrentPage(newPage);
  };

  const onSortingChange = (sortingState) => {
    if (sortingState && sortingState.length > 0) {
      const columnId = sortingState[0].id;
      setSortColumn(columnId);
      setSortType(sortingState[0].desc ? 'descending' : 'ascending');
      setCurrentPage(1);
    }
  };

  const handleRowClick = (rowData) => {
    if (onNavigate) {
      onNavigate(`/admin/roles/${rowData.role_uuid || rowData.id}`);
    }
  };

  const columns = React.useMemo(() => [
    columnHelper.accessor('title', {
      header: Strings.title || 'Title',
      cell: info => info.getValue() || '-',
    }),
    columnHelper.accessor('archetype', {
      header: Strings.archetype || 'Archetype',
      cell: info => info.getValue() || '-',
    }),
    columnHelper.accessor('organization', {
      accessorFn: row => row.org_name,
      header: Strings.organization || 'Organization',
      cell: info => info.getValue() || '-',
    }),
  ], []);

  const getHeaderComponents = () => (
    <>
      <TextInput
        class="search"
        placeholder={Strings.search}
        value={searchTerm}
        onChange={onSearchChange}
      />
      <OrgSelect />
      <AdvancedFilters>
        <Filter definition={getFiltersDefinition().archetypeExtended} />
      </AdvancedFilters>

      <Button
        class={HEADER_BUTTON_BLUE}
        onClick={() => addRole(() => loadRoles(true))}
        title={`+ ${Strings.create || 'Create'}`}
      />
    </>
  );

  return (
    <>
      <PageHeader left={Strings.roles || 'Roles'} right={getHeaderComponents()} />
      <AdvancedFiltersBar />
      <Table3
        data={roles || []}
        columns={columns}
        isLoading={isLoading}
        pagination={pagination}
        onOffsetChange={onOffsetChange}
        onSortingChange={onSortingChange}
        onRowClick={handleRowClick}
        noDataText={Strings.noRolesFound || 'No roles found'}
      />
    </>
  );
}

const mapStateToProps = state => ({
  orgIds: state.entities?.orgFilter?.orgIds,
  filters: state.entities.advancedFilters.filters.items,
});

const mapDispatchToProps = dispatch => ({
  getRoles: params => dispatch(actions.getRoles(params)),
  onNavigate: path => dispatch(push(path)),
  addRole: onSuccess => dispatch(openModalAction('create-role', {onSuccess})),
});

export default connect(mapStateToProps, mapDispatchToProps)(AdminRolesTab);
